import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  Button,
  FormControl,
  Snackbar,
  Alert,
  Grid,
  useTheme,
  useMediaQuery,
  Paper,
  Collapse,
  IconButton,
} from "@mui/material";
import { ExpandMore, FilterList } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { parseDate, getLocalTimeZone } from '@internationalized/date';
import DatePickerField from "../../../common/DatePickerDashboard";
import SendMessageButton from "./SendMessageButton";
import Email from "./Email";
import Sms from "./Sms";
import Phone from "./Phone";
import {
  getAllEmailAction,
  searchAction,
  getAllSmsAction,
  getAllPhoneAction,
  sendMessageAction,
} from "../../../redux/actions/communicationAction";

const poppinsFont = {
  fontFamily: '"Poppins", sans-serif',
};

const CommunicationTable = ({
  id,
  policyId,
  customerId,
}) => {
  const [subject, setSubject] = useState("");
  const [communicationType, setCommunicationType] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDialogClose, setIsDialogClose] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // const [fromDate, setFromDate] = useState(dayjs().startOf('month'));
  // const [toDate, setToDate] = useState(dayjs());

  const [emailPage, setEmailPage] = useState(0);
  const [rowsPerPageEmail, setRowsPerPageEmail] = useState(5);
  const [smsPage, setSmsPage] = useState(0);
  const [rowsPerPageSms, setRowsPerPageSms] = useState(5);
  const [phonePage, setPhonePage] = useState(0);
  const [rowsPerPagePhone, setRowsPerPagePhone] = useState(5);

  const getToday = useCallback(() => {
    const today = new Date();
    return parseDate(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    );
  }, []);

  const getFirstOfMonth = useCallback(() => {
    const today = new Date();
    return parseDate(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`
    );
  }, []);

  const formatForForm = useCallback((date) => {
    const d = date.toDate(getLocalTimeZone());
    return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
  }, []);

  const initialFormData = useMemo(() => ({
    fromDate: formatForForm(getFirstOfMonth()),
    toDate: formatForForm(getToday()),
  }), [formatForForm, getFirstOfMonth, getToday]);

  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const smspagination = useSelector(
    (state) => state.communication?.allSms.content || []
  );
  const sms_pagination = useSelector(
    (state) => state.communication?.allSms || []
  );

  const phonesPagination = useSelector(
    (state) => state.communication?.allPhones || { totalElements: 0, page: 0 }
  );

  const dispatch = useDispatch();

  const phones = phonesPagination?.content || [];

  const handleDialogOpen = useCallback(() => setIsDialogOpen(true), []);
  const handleDialogClose = useCallback(() => setIsDialogClose(false), []);

  const handleSubjectChange = useCallback((e) => {
    setSubject(e.target.value);
  }, []);

  const handleCommunicationTypeChange = useCallback((e) => {
    setCommunicationType(e.target.value);
  }, []);

  const handleDateChange = useCallback((fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleBlur = useCallback((fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
  }, []);

  const handleSearch = useCallback(() => {
    const baseParams = {
      fromDate: formData.fromDate
        ? dayjs(formData.fromDate).add(1, "day").utc().startOf("day").toISOString()
        : "",
      toDate: formData.toDate
        ? dayjs(formData.toDate).add(1, "day").utc().endOf("day").toISOString()
        : "",
      subject,
    };
    setEmailPage(0);
    setSmsPage(0);
    setPhonePage(0);

    if (communicationType === "") {
      dispatch(searchAction({ ...baseParams, communicationType: "email" }, 0, rowsPerPageEmail, () => setHasSearched(true)));
      dispatch(searchAction({ ...baseParams, communicationType: "sms" }, 0, rowsPerPageSms, () => setHasSearched(true)));
      dispatch(searchAction({ ...baseParams, communicationType: "phone" }, 0, rowsPerPagePhone, () => setHasSearched(true)));
    } else {
      dispatch(searchAction({ ...baseParams, communicationType }, 0, 5, () => setHasSearched(true)));
    }

    if (isMobile) {
      setFiltersExpanded(false);
    }
  }, [formData, subject, communicationType, rowsPerPageEmail, rowsPerPageSms, rowsPerPagePhone, dispatch, isMobile]);

  const handleSendMessage = useCallback(async () => {
    const payload = {
      toPhoneNumber: phoneNumber,
      messageBody: message,
    };

    try {
      await dispatch(sendMessageAction(payload));
      setSnackbar({
        open: true,
        message: "Message sent successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to send the message. Please try again.",
        severity: "error",
      });
    }

    handleDialogClose();
  }, [phoneNumber, message, dispatch, handleDialogClose]);

  const resetSearch = useCallback(() => {
    setHasSearched(false);
    // setFromDate(dayjs().startOf("month"));
    // setToDate(dayjs());
    setFormData({
      fromDate: formatForForm(getFirstOfMonth()),
      toDate: formatForForm(getToday()),
    });
    setSubject("");
    setCommunicationType("");
    setFieldErrors({});
    setTouchedFields({});

    setEmailPage(0);
    setSmsPage(0);
    setPhonePage(0);

    dispatch(getAllEmailAction(0, rowsPerPageEmail));
    dispatch(getAllSmsAction(0, rowsPerPageSms));
    dispatch(getAllPhoneAction(0, rowsPerPagePhone));
  }, [formatForForm, getFirstOfMonth, getToday, dispatch, rowsPerPageEmail, rowsPerPageSms, rowsPerPagePhone]);

  // const [selectedPolicyId, setSelectedPolicyId] = useState("");
  // const [selectedOption, setSelectedOption] = useState("All");
  const [openSendDialog, setOpenSendDialog] = useState(false);

  const MobileFilterHeader = useMemo(() => (
    <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mx: { xs: 1, sm: 2 },
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="primary" />
            <Typography variant="h6" sx={{ ...poppinsFont, fontWeight: 500 }}>
              Filters
            </Typography>
          </Box>
          <IconButton
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            sx={{
              transform: filtersExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease'
            }}
          >
            <ExpandMore />
          </IconButton>
        </Box>

        <Collapse in={filtersExpanded}>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, ...poppinsFont, fontWeight: 500 }}>
                  From Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePickerField
                    fieldName="fromDate"
                    formData={formData}
                    handleDateChange={handleDateChange}
                    handleBlur={handleBlur}
                    touchedFields={touchedFields}
                    fieldErrors={fieldErrors}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, ...poppinsFont, fontWeight: 500 }}>
                  To Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePickerField
                    fieldName="toDate"
                    formData={formData}
                    handleDateChange={handleDateChange}
                    handleBlur={handleBlur}
                    touchedFields={touchedFields}
                    fieldErrors={fieldErrors}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, ...poppinsFont, fontWeight: 500 }}>
                  Subject
                </Typography>
                <TextField
                  fullWidth
                  value={subject}
                  onChange={handleSubjectChange}
                  size="small"
                  sx={{ ...poppinsFont }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, ...poppinsFont, fontWeight: 500 }}>
                  Communication Type
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={communicationType}
                    onChange={handleCommunicationTypeChange}
                    sx={{ ...poppinsFont }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="sms">SMS</MenuItem>
                    <MenuItem value="phone">Phone</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  <Button
                    onClick={handleSearch}
                    variant="contained"
                    className="btn-primary"
                    size={isSmallMobile ? "small" : "medium"}
                    sx={{
                      textTransform: "none",
                      ...poppinsFont,
                      fontSize: isSmallMobile ? "12px" : "14px",
                      flex: { xs: '1 1 120px', sm: 'none' },
                      minWidth: '100px'
                    }}
                    disabled={!formData.fromDate || !formData.toDate}
                  >
                    Search
                  </Button>

                  <Button
                    onClick={resetSearch}
                    variant="outlined"
                    className="btn-secondary"
                    size={isSmallMobile ? "small" : "medium"}
                    sx={{
                      textTransform: "none",
                      ...poppinsFont,
                      fontSize: isSmallMobile ? "12px" : "14px",
                      flex: { xs: '1 1 120px', sm: 'none' },
                      minWidth: '100px'
                    }}
                  >
                    Reset
                  </Button>

                  <Button
                    variant="contained"
                    onClick={() => setOpenSendDialog(true)}
                  >
                    Send Email
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  ), [
    filtersExpanded,
    formData,
    handleDateChange,
    handleBlur,
    touchedFields,
    fieldErrors,
    subject,
    handleSubjectChange,
    communicationType,
    handleCommunicationTypeChange,
    handleSearch,
    resetSearch,
    isSmallMobile
  ]);

  // Memoize the desktop filters to prevent re-renders
  const DesktopFilters = useMemo(() => (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        gap: "20px",
        alignItems: "center",
        justifyContent: "flex-start",
        mb: 2,
        padding: "0 20px",
        boxSizing: "border-box",
        flexWrap: 'wrap'
      }}
    >
      <Box
        sx={{
          width: "210px",
          height: "50px",
          marginTop: "-30px",
        }}
      >
        <Typography variant="body1" sx={{ mb: 0.5, ...poppinsFont }}>
          From
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePickerField
            fieldName="fromDate"
            formData={formData}
            handleDateChange={handleDateChange}
            handleBlur={handleBlur}
            touchedFields={touchedFields}
            fieldErrors={fieldErrors}
          />
        </LocalizationProvider>
      </Box>

      <Box
        sx={{
          width: "210px",
          height: "50px",
          marginTop: "-30px",
        }}
      >
        <Typography variant="body1" sx={{ mb: 0.5, ...poppinsFont }}>
          To
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePickerField
            fieldName="toDate"
            formData={formData}
            handleDateChange={handleDateChange}
            handleBlur={handleBlur}
            touchedFields={touchedFields}
            fieldErrors={fieldErrors}
          />
        </LocalizationProvider>
      </Box>

      <Box
        sx={{
          width: "180px",
          height: "50px",
          marginTop: "-30px",
        }}
      >
        <Typography variant="body1" sx={{ mb: 0.5, ...poppinsFont }}>
          Subject
        </Typography>
        <TextField
          sx={{
            ...poppinsFont,
            width: "180px",
            "& .MuiInputBase-root": {
              height: "56px",
              display: "flex",
              alignItems: "center",
            },
            "& input": { height: "100%" },
          }}
          value={subject}
          onChange={handleSubjectChange}
          size="small"
          autoComplete="off"
        />
      </Box>

      <Box
        sx={{
          height: "50px",
          marginTop: "-30px",
        }}
      >
        <FormControl sx={{ width: "150px" }}>
          <Typography variant="body1" sx={{ mb: 0.5, ...poppinsFont }}>
            Select to search
          </Typography>
          <Select
            value={communicationType}
            onChange={handleCommunicationTypeChange}
            native
            inputProps={{
              name: 'communicationType',
              id: 'communication-type-native',
            }}
            size="small"
            sx={{
              height: "56px",
              "& .MuiInputBase-root": {
                height: "56px",
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            <option value="">All</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="phone">Phone</option>
          </Select>
        </FormControl>
      </Box>

      <Box>
        <Button
          onClick={handleSearch}
          variant="outlined"
          size="large"
          sx={{
            textTransform: "none",
            marginTop: "30px",
            ...poppinsFont,
          }}
          disabled={!formData.fromDate || !formData.toDate}
        >
          Search
        </Button>
      </Box>

      <Box>
        <Button
          onClick={resetSearch}
          variant="outlined"
          size="large"
          sx={{
            textTransform: "none",
            marginTop: "30px",
            ...poppinsFont,
          }}
        >
          Reset
        </Button>

        <Button
        variant="outlined"
          sx={{
            textTransform: "none",
            marginTop: "30px",
            marginLeft: '20px',
          }}
          onClick={() => setOpenSendDialog(true)}
        >
          Send Email
        </Button>
      </Box>
    </Box>
  ), [
    formData,
    handleDateChange,
    handleBlur,
    touchedFields,
    fieldErrors,
    subject,
    handleSubjectChange,
    communicationType,
    handleCommunicationTypeChange,
    handleSearch,
    resetSearch
  ]);

  return (
    <Box>
      {MobileFilterHeader}
      {DesktopFilters}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {(communicationType === "email" || !communicationType) && (
        <>
          <Email
            poppinsFont={poppinsFont}
            openSendDialog={openSendDialog}
            setOpenSendDialog={setOpenSendDialog}
            policyId={id}
            hasSearched={hasSearched}
            formData={{
              fromDate: dayjs(formData.fromDate).utc().startOf("day").toISOString(),
              toDate: dayjs(formData.toDate).utc().endOf("day").toISOString(),
              subject,
              communicationType: "email",
            }}
            page={emailPage}
            size={rowsPerPageEmail}
            setPage={setEmailPage}
            setSize={setRowsPerPageEmail}
          />

          <SendMessageButton
            handleDialogOpen={handleDialogOpen}
            handleDialogClose={handleDialogClose}
            handleSendMessage={handleSendMessage}
            isDialogOpen={isDialogOpen}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            message={message}
            setMessage={setMessage}
            snackbar={snackbar}
            setSnackbar={setSnackbar}
          />
        </>
      )}

      {(communicationType === "sms" || !communicationType) && (
        <Sms
          data={smspagination}
          pagination={sms_pagination}
          customerId={customerId}
          policyNumber={policyId}
          policyId={id}
          page={smsPage}
          size={rowsPerPageSms}
          setPage={setSmsPage}
          setSize={setRowsPerPageSms}
          hasSearched={hasSearched}
          formData={{
            fromDate: dayjs(formData.fromDate).utc().startOf("day").toISOString(),
            toDate: dayjs(formData.toDate).utc().endOf("day").toISOString(),
            subject,
            communicationType: "sms",
          }}
        />
      )}
      {(communicationType === "phone" || !communicationType) && (
        <Phone
          data={phones}
          page={phonePage}
          size={rowsPerPagePhone}
          setPage={setPhonePage}
          setSize={setRowsPerPagePhone}
          hasSearched={hasSearched}
          formData={{
            fromDate: dayjs(formData.fromDate).utc().startOf("day").toISOString(),
            toDate: dayjs(formData.toDate).utc().endOf("day").toISOString(),
            subject,
            communicationType: "phone",
          }}
        />
      )}
    </Box>
  );
};

export default CommunicationTable;